import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getCallFormStatePermissions, getCallFormStates } from '../../../../../Api/ApiMethods';

const BASE_TRANSITIONS = {
    'Taslak': { save: 'Eksper Onay Bekleniyor', draft: 'Taslak' },
    'Yeni Çağrı Formu': { save: 'Eksper Onay Bekleniyor', draft: 'Taslak' },
    'Eksper Onay Bekleniyor': { approve: 'Yönetici Onay Bekleniyor', reject: 'Çağrı Formu Reddedildi', return: 'Düzeltme Bekleniyor' },
    'Yönetici Onay Bekleniyor': { approve: 'Ödeme Bekleniyor', reject: 'Çağrı Formu Reddedildi', return: 'Düzeltme Bekleniyor' },
    'Ödeme Bekleniyor': { complete: 'Vekalet Bekleniyor', correction: 'Düzeltme Bekleniyor', reject: 'Müvekkil Reddedildi', addExpense: 'Ödeme Bekleniyor' },
    'Vekalet Bekleniyor': { received: 'Evrak Bekleniyor', pending: 'Vekalet Bekleniyor' },
    'Evrak Bekleniyor': { received: 'Eksper İşlem Bekleniyor', pending: 'Evrak Bekleniyor' },
    'Eksper İşlem Bekleniyor': { next: 'Eksper Rapor Bekleniyor', pending: 'Eksper İşlem Bekleniyor' },
    'Eksper Rapor Bekleniyor': { next: 'Eksper Raporu Ödeme Bekleniyor', pending: 'Eksper Rapor Bekleniyor' },
    'Eksper Raporu Ödeme Bekleniyor': { next: 'Sigorta Başvuru Dilekçe Bekleniyor', pending: 'Eksper Raporu Ödeme Bekleniyor', addExpense: 'Eksper Raporu Ödeme Bekleniyor' },
    'Sigorta Başvuru Dilekçe Bekleniyor': { next: 'Yeni Eksper İşlem Bekleniyor', pending: 'Sigorta Başvuru Dilekçe Bekleniyor', withoutExpert: 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor' },
    'Yeni Eksper İşlem Bekleniyor': { next: 'Yeni Eksper Rapor Bekleniyor', pending: 'Yeni Eksper İşlem Bekleniyor' },
    'Yeni Eksper Rapor Bekleniyor': { next: 'Yeni Eksper Raporu Ödeme Bekleniyor', pending: 'Yeni Eksper Rapor Bekleniyor' },
    'Yeni Eksper Raporu Ödeme Bekleniyor': { next: 'Yeni Sigorta Başvuru Dilekçe Bekleniyor', pending: 'Yeni Eksper Raporu Ödeme Bekleniyor', addExpense: 'Yeni Eksper Raporu Ödeme Bekleniyor' },
    'Yeni Sigorta Başvuru Dilekçe Bekleniyor': { next: 'Yeni Eksper İşlem Bekleniyor', pending: 'Yeni Sigorta Başvuru Dilekçe Bekleniyor' },
    'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor': { next: 'Yeni Eksper İşlem Bekleniyor', pending: 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor' },
    'Düzeltme Bekleniyor': { update: 'Eksper Onay Bekleniyor', expertApproval: 'Eksper Onay Bekleniyor', managerApproval: 'Yönetici Onay Bekleniyor' },
    'Tüm Form': { save: 'Tüm Form' },
};

const buildTransitions = (statesByTitle) => {
    const generatedTransitions = {};

    for (const currentStateName in BASE_TRANSITIONS) {
        if (!statesByTitle[currentStateName]) {
            continue;
        }

        generatedTransitions[currentStateName] = {};

        for (const action in BASE_TRANSITIONS[currentStateName]) {
            const nextStateName = BASE_TRANSITIONS[currentStateName][action];
            const nextStateId = statesByTitle[nextStateName];

            if (nextStateId !== undefined) {
                generatedTransitions[currentStateName][action] = nextStateId;
            }
        }
    }

    return generatedTransitions;
};

const useCallFormWorkflow = ({ id }) => {
    const [currentFormState, setCurrentFormState] = useState(id ? null : 'Yeni Çağrı Formu');
    const [fieldPermissions, setFieldPermissions] = useState([]);
    const [dynamicCallFormStates, setDynamicCallFormStates] = useState(null);
    const [dynamicStateTransitions, setDynamicStateTransitions] = useState(null);
    const [loadingStates, setLoadingStates] = useState(true);

    const fetchFieldPermissions = async (stateId) => {
        if (!stateId) {
            setFieldPermissions([]);
            return;
        }

        try {
            const response = await getCallFormStatePermissions(stateId);

            if (response?.isSuccess && response?.data) {
                setFieldPermissions(response.data.fieldPermissions || []);
            } else {
                toast.error('Alan izinleri yüklenemedi.');
                setFieldPermissions([]);
            }
        } catch (error) {
            toast.error('Alan izinleri yüklenirken bir hata oluştu.');
            setFieldPermissions([]);
        }
    };

    useEffect(() => {
        const fetchAndProcessStates = async () => {
            setLoadingStates(true);
            try {
                const response = await getCallFormStates();

                if (response?.isSuccess && response?.data?.items) {
                    const fetchedStates = response.data.items.reduce((accumulator, state) => {
                        if (state.title && state.id) {
                            accumulator[state.title] = state.id;
                        }
                        return accumulator;
                    }, {});

                    setDynamicCallFormStates(fetchedStates);
                    setDynamicStateTransitions(buildTransitions(fetchedStates));
                } else {
                    toast.error('Çağrı formu durumları yüklenemedi.');
                    setDynamicCallFormStates({});
                    setDynamicStateTransitions({});
                }
            } catch (error) {
                toast.error('Çağrı formu durumları yüklenirken bir hata oluştu.');
                setDynamicCallFormStates({});
                setDynamicStateTransitions({});
            } finally {
                setLoadingStates(false);
            }
        };

        fetchAndProcessStates();
    }, []);

    useEffect(() => {
        if (!id && dynamicCallFormStates && !loadingStates) {
            const initialStateTitle = 'Yeni Çağrı Formu';
            const initialStateId = dynamicCallFormStates[initialStateTitle];

            if (initialStateId) {
                fetchFieldPermissions(initialStateId);
                setCurrentFormState(initialStateTitle);
            } else {
                setFieldPermissions([]);
                setCurrentFormState(initialStateTitle);
            }
        }
    }, [id, dynamicCallFormStates, loadingStates]);

    return {
        currentFormState,
        setCurrentFormState,
        fieldPermissions,
        setFieldPermissions,
        dynamicCallFormStates,
        dynamicStateTransitions,
        loadingStates,
        fetchFieldPermissions,
    };
};

export default useCallFormWorkflow;
